import { Chart, registerables } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { useEffect, useRef } from "react";

import { formatNumber } from "utils/formatNumber";

Chart.register(...registerables);

const formatData = ({ data, backgroundColor, barThickness } = {}) => {
  const modifiedDatasets = data?.datasets?.map((el) => ({
    backgroundColor,
    barThickness,
    datalabels: {
      anchor: "end",
      align: "end",
      formatter: function (value) {
        return formatNumber.toAmount(value?.x ?? value);
      },
    },
    ...el,
  }));

  return {
    labels: data?.labels,
    datasets: modifiedDatasets,
  };
};

const StackedBarHorizontalChart = (props) => {
  const {
    data = [],
    height = 200,
    id = "bar-chart",
    barThickness = 20,
    borderRadius = 100,
    dataBgColor = "#FFB703",
  } = props;

  const chartRef = useRef(null);

  useEffect(() => {
    const createChart = (chartData) => {
      const barPosition = {
        id: "barPosition",
        beforeDatasetDraw: (chart) => {
          const {
            data,
            chartArea: { height: chartAreaHeight, top: chartAreaTop },
          } = chart;
          const barHeight = chartAreaHeight / data.labels.length;

          let spacingModifier = 0.5;
          let stackName = chart?.config?.data?.datasets?.find(
            (el) => el?.stack,
          )?.stack;
          chart?.config?.data?.datasets?.forEach((el, elIdx) => {
            if (!el?.stack || el?.stack !== stackName) {
              stackName = el?.stack;
              spacingModifier += 1;
            }
            const datasetMeta = chart.getDatasetMeta(elIdx);

            datasetMeta?.data?.forEach((dataPoint) => {
              dataPoint.y = chartAreaTop + barHeight * spacingModifier;
            });
          });
        },
      };
      const ctx = document.getElementById(id)?.getContext("2d");
      chartRef.current = new Chart(ctx, {
        type: "bar",
        data: chartData,
        plugins: [ChartDataLabels, barPosition],
        options: {
          responsive: true,
          maintainAspectRatio: false,
          elements: {
            bar: {
              borderRadius,
              borderSkipped: false,
            },
          },
          plugins: {
            legend: {
              position: "bottom",
              labels: {
                filter: (item) => {
                  const foundData = chartData?.datasets?.find(
                    (el) => el?.label === item?.text,
                  );
                  return foundData?.showLegend;
                },
              },
            },
          },
          scales: {
            y: {
              grid: { display: false },
              position: "left",
              ticks: {
                color: "#5F737F",
                font: {
                  size: 11,
                },
              },
            },
            x: {
              min: 0,
              grace: "100%",
            },
          },
          indexAxis: "y",
        },
      });
    };

    if (chartRef.current) {
      chartRef.current.destroy();
    }

    createChart(
      formatData({ data, backgroundColor: dataBgColor, barThickness }),
    );

    return () => {
      chartRef.current.destroy();
    };
  }, [barThickness, borderRadius, data, dataBgColor, id, height]);

  return (
    <div className='w-full'>
      <canvas id={id} width='400' height={height}></canvas>
    </div>
  );
};

export default StackedBarHorizontalChart;
